import _extends from "@babel/runtime/helpers/extends";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
/* © 2022 Promethean. All Rights Reserved.
 *
 * Unauthorized copying of this file or any part of this file
 * via any medium is strictly prohibited.
 */
import React, { useEffect, useRef, useState, useMemo } from 'react';
import classnames from 'classnames';
import { ThemeProvider } from '@mui/material/styles';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormLabel from '@mui/material/FormLabel';
import FormControl from '@mui/material/FormControl';
import useId from '@mui/utils/useId';
import { createRadioTheme, ROOT_PREFIX } from './radioGroupStyles';
import { EmbersLabeledBox } from '../labeledBox/labeledBox';
import { LABEL_FOCUS_VISIBLE_CLASS } from '../labeledBox/labeledBoxStyles';
export function EmbersRadioGroup(_ref) {
  var className = _ref.className,
    groupLabel = _ref.groupLabel,
    name = _ref.name,
    valueProp = _ref.value,
    onChange = _ref.onChange,
    options = _ref.options,
    row = _ref.row,
    _ref$labelClickable = _ref.labelClickable,
    labelClickable = _ref$labelClickable === void 0 ? true : _ref$labelClickable;
  var theme = createRadioTheme();
  var _useState = useState(valueProp),
    _useState2 = _slicedToArray(_useState, 2),
    value = _useState2[0],
    setValue = _useState2[1];
  useEffect(function () {
    setValue(valueProp);
  }, [valueProp]);
  var radioRefs = useRef(new Set());
  var collectRadioRefs = function collectRadioRefs(radioElem) {
    if (radioElem && radioElem.parentElement) {
      radioRefs.current.add(radioElem.parentElement);
    }
  };
  var setHoverStyle = function setHoverStyle(radioRef) {
    radioRef.classList.add('EmbersRadio-hybridDeviceHover');
  };
  var removeHoverStyle = function removeHoverStyle(radioRef) {
    if (_toConsumableArray(radioRef.classList).includes('EmbersRadio-hybridDeviceHover')) radioRef.classList.remove('EmbersRadio-hybridDeviceHover');
  };
  function onMouseEnter() {
    // On PCs/laptops with touch screen, @media (hover: hover) and @media (any-hover: hover) cannot triggered,
    // and @media (any-pointer: coarse) and @media (pointer: coarse) cannot distinguish them with actual mobile devices,
    // so we need to handle mouse events on those PCs/laptops(on mobile devices there should not be mouse events)
    if (window.navigator.maxTouchPoints > 0) setHoverStyle(this);
  }
  function onMouseLeave() {
    if (window.navigator.maxTouchPoints > 0) removeHoverStyle(this);
  }
  useEffect(function () {
    var _radioRefs$current;
    (_radioRefs$current = radioRefs.current) === null || _radioRefs$current === void 0 || _radioRefs$current.forEach(function (radioRef) {
      radioRef.addEventListener('mouseenter', onMouseEnter);
      radioRef.addEventListener('mouseleave', onMouseLeave);
    });
    return function () {
      var _radioRefs$current2;
      (_radioRefs$current2 = radioRefs.current) === null || _radioRefs$current2 === void 0 || _radioRefs$current2.forEach(function (radioRef) {
        radioRef.removeEventListener('mouseenter', onMouseEnter);
        radioRef.removeEventListener('mouseleave', onMouseLeave);
      });
    };
  }, []);

  // updateLabelClassList() moves the "focus-visible" class out to the label,
  //  to allow focus to be displayed around the label instead of just around the radio
  var updateLabelClassList = function updateLabelClassList() {
    if (radioRefs.current) {
      radioRefs.current.forEach(function (radioElem) {
        var embersLabelBox = radioElem.parentElement;
        if (embersLabelBox) {
          if (radioElem.classList.contains('Mui-focusVisible')) {
            embersLabelBox.classList.add(LABEL_FOCUS_VISIBLE_CLASS);
          } else {
            embersLabelBox.classList.remove(LABEL_FOCUS_VISIBLE_CLASS);
          }
        }
      });
    }
  };
  useEffect(updateLabelClassList);

  // the focus-visible class info needs to be updated after the radio group
  //  gains loses focus; the timeout is because it takes a beat after the group
  //  focus changes until the radios to reset their classlist
  var handleGroupFocusChange = function handleGroupFocusChange() {
    setTimeout(updateLabelClassList, 10);
  };
  var onDataChange = function onDataChange(event) {
    setValue(event.target.value);
    onChange === null || onChange === void 0 || onChange(event);
  };
  var labelId = useId();
  var formLabelProps = useMemo(function () {
    return {
      id: labelId
    };
  }, [labelId]);
  var extraRadioGroupProps = useMemo(function () {
    var extraRadioGroupProps = {};
    if (groupLabel && labelId) {
      extraRadioGroupProps['aria-labelledby'] = labelId;
    } else if (name) {
      extraRadioGroupProps['aria-label'] = name;
    }
    return extraRadioGroupProps;
  }, [groupLabel, name, labelId]);
  return /*#__PURE__*/React.createElement(ThemeProvider, {
    theme: theme
  }, /*#__PURE__*/React.createElement(FormControl, {
    className: classnames("".concat(ROOT_PREFIX, "-root"), className)
  }, groupLabel && /*#__PURE__*/React.createElement(FormLabel, formLabelProps, groupLabel), /*#__PURE__*/React.createElement(RadioGroup, _extends({
    name: name,
    value: value,
    onBlur: handleGroupFocusChange,
    onFocus: handleGroupFocusChange,
    onChange: onDataChange,
    row: row
  }, extraRadioGroupProps), options.map(function (item) {
    var RadioItem = /*#__PURE__*/React.createElement(Radio, {
      value: item.value,
      checked: value === item.value,
      onChange: onDataChange,
      disableRipple: true,
      inputRef: collectRadioRefs,
      disabled: item.disabled
    });
    return /*#__PURE__*/React.createElement(EmbersLabeledBox, {
      key: item.value,
      labelClickable: labelClickable,
      label: item.label,
      formControlProps: {
        value: item.value,
        disabled: item.disabled,
        control: RadioItem,
        label: item.label
      }
    }, RadioItem);
  }))));
}